import { keyframes, styled } from "goober";
import { useMemo, useRef } from "react";
import { useMouseTracking } from "../hooks/useMouseTracking";
import { getUserColor } from "./Avatar";

// The bot face is designed at this reference size, then scaled.
const REF = 24;

// The bot art sits at ~75% of the avatar footprint so it reads at its design
// size and matches the visual weight of a member avatar rather than filling
// the whole slot.
const ART_RATIO = 0.75;

const blink = keyframes`
  0%, 90%, 100% { transform: scaleY(1); }
  95% { transform: scaleY(0.05); }
`;

const Container = styled("div")`
  position: relative;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Art = styled("div")`
  position: relative;
  line-height: 0;
`;

const EyeOverlay = styled("div")`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EyeRow = styled("div")`
  display: flex;
  align-items: center;
  animation: ${blink} 4s ease-in-out infinite;
`;

const Eye = styled("div")`
  border-radius: 50%;
`;

function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++)
    hash = (hash * 31 + str.charCodeAt(i)) | 0;
  return hash >>> 0;
}

export function ServiceAccountAvatar({
  seed = "service_account",
  size = 32,
}: {
  seed?: string;
  size?: number;
}) {
  const art = size * ART_RATIO;
  const scale = art / REF;
  const faceRef = useRef<HTMLDivElement>(null);
  // The bot art is drawn at ART_RATIO scale, so the offset is bumped to match
  // the on-screen eye travel of a member avatar (which applies size * 0.06 then
  // scales its face up).
  useMouseTracking(faceRef, size * 0.08);

  const color = useMemo(() => getUserColor(seed), [seed]);
  // Stagger blink per service account so a list of them doesn't blink in sync.
  const animDelay = useMemo(
    () => `${-(hashString(seed) % 4000) / 1000}s`,
    [seed]
  );

  return (
    <Container style={{ width: size, height: size }}>
      <Art style={{ width: art, height: art }}>
        <svg
          width={art}
          height={art}
          viewBox="0 0 24 24"
          fill="none"
          style={{ display: "block" }}
        >
          <rect
            x="0.25"
            y="6.25"
            width="23.5"
            height="15.5"
            rx="7.75"
            fill={`var(--${color}-600)`}
            stroke={`var(--${color}-600)`}
            strokeWidth="0.5"
          />
          <path d="M12 6L12 1" stroke={`var(--${color}-600)`} />
          <circle cx="12" cy="2" r="1.5" fill={`var(--${color}-600)`} />
        </svg>
        {/* Eyes sit at y=14 of the 24-box (just below center), so pad the
            flex-centered overlay down by art/6. */}
        <EyeOverlay style={{ paddingTop: art / 6 }}>
          <div ref={faceRef}>
            <EyeRow style={{ gap: 2 * scale, animationDelay: animDelay }}>
              <Eye
                style={{
                  width: 6 * scale,
                  height: 6 * scale,
                  background: `var(--${color}-1000)`,
                }}
              />
              <Eye
                style={{
                  width: 6 * scale,
                  height: 6 * scale,
                  background: `var(--${color}-1000)`,
                }}
              />
            </EyeRow>
          </div>
        </EyeOverlay>
      </Art>
    </Container>
  );
}
